import {
  ApplicationFullStatus,
  ApplicationLearningSummary,
  LearningGlobalSummary,
  BulkRefreshStatus,
  FilterState,
  PaginatedResponse,
} from './types';

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('token');

  const headers = {
    ...options.headers,
    ...(token ? { 'Authorization': token } : {}),
    'Accept': 'application/json',
  };

  const res = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }

  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return res.json();
}

export async function fetchOverview(
  page: number,
  size: number,
  fetchAll: boolean = false
): Promise<PaginatedResponse<ApplicationLearningSummary>> {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  if (fetchAll) params.set('fetch_all', 'true');

  return request(`/applications/learning/overview?${params.toString()}`);
}

export async function searchApplications(
  query: string,
  searchType: 'organization_name' | 'department_name' | 'specialist_query' | 'student_query',
  page: number,
  size: number,
  filters: FilterState,
  fetchAll: boolean = false
): Promise<PaginatedResponse<ApplicationLearningSummary>> {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  if (fetchAll) params.set('fetch_all', 'true');

  // Записываем значение главной поисковой строки в выбранный тип параметра
  if (query.trim()) {
    params.set(searchType, query.trim());
  }

  // Записываем общие фильтры
  if (filters.overall_status) params.set('overall_status', filters.overall_status);
  if (filters.nearest_deadline_from) params.set('nearest_deadline_from', filters.nearest_deadline_from);
  if (filters.nearest_deadline_to) params.set('nearest_deadline_to', filters.nearest_deadline_to);
  if (filters.sort_by) params.set('sort_by', filters.sort_by);
  if (filters.sort_order) params.set('sort_order', filters.sort_order);

  // Записываем точечные фильтры из панели фильтров, только если они не перекрываются с главным поиском
  if (filters.department_name?.trim() && searchType !== 'department_name') {
    params.set('department_name', filters.department_name.trim());
  }
  if (filters.specialist_query?.trim() && searchType !== 'specialist_query') {
    params.set('specialist_query', filters.specialist_query.trim());
  }
  if (filters.student_query?.trim() && searchType !== 'student_query') {
    params.set('student_query', filters.student_query.trim());
  }

  return request(`/applications/learning/search?${params.toString()}`);
}

export async function startBulkRefresh(): Promise<BulkRefreshStatus> {
  return request('/applications/learning/bulk-refresh/start?force_all=true', { method: 'POST' });
}

export async function getBulkRefreshStatus(): Promise<BulkRefreshStatus> {
  return request('/applications/learning/bulk-refresh/status');
}

export async function fetchGlobalSummary(): Promise<LearningGlobalSummary> {
  return request('/applications/learning/summary');
}

export async function fetchFullStatus(
  applicationId: number,
  forceRefresh: boolean = false,
  includeModules: boolean = false
): Promise<ApplicationFullStatus> {
  const params = new URLSearchParams();
  if (forceRefresh) params.set('force_refresh', 'true');
  if (includeModules) params.set('include_modules', 'true');

  return request(`/applications/${applicationId}/learning-status?${params.toString()}`);
}

export async function downloadCompletionArchive(applicationId: number): Promise<void> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    'Accept': 'application/zip, application/octet-stream, */*',
  };

  if (token) {
    headers['Authorization'] = token;
  }

  const res = await fetch(`${API_BASE}/applications/${applicationId}/completion-archive`, {
    method: 'GET',
    headers,
  });

  if (res.status === 401) {
    throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
  }

  const contentType = res.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Ошибка сервера (HTTP ${res.status})`);
  }

  if (!res.ok) {
    throw new Error(`Ошибка HTTP ${res.status}: ${res.statusText}`);
  }

  const blob = await res.blob();

  if (blob.size === 0) {
    throw new Error('Сервер вернул пустой файл (0 байт). Возможно, нет данных для архивации.');
  }

  const contentDisposition = res.headers.get('Content-Disposition');
  let filename = `application_${applicationId}_archive.zip`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    if (match && match[1]) {
      filename = decodeURIComponent(match[1]);
    }
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();

  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}